// ./src/QrScanner/cameraChoices.ts
//
// The cameras a phone offers, as a person reads them (owner, 2026-11-02: "the camera change button just iterates between
// cameras and it is hard to use when there are like 4 cameras and it's hard to see which one is active now").
//
// ⚠ Pure and import-free: `scripts/checkScanPages.ts` (apps/app) RUNS it.

export interface CameraChoice {
  id: string;
  label: string;
  /** Which way it faces, as far as its label says. */
  facing: "back" | "front" | "unknown";
}

const facingOf = (label: string): CameraChoice["facing"] => {
  if (/\b(back|rear|environment)\b/i.test(label)) {
    return "back";
  }

  return /\b(front|user|facetime)\b/i.test(label) ? "front" : "unknown";
};

/**
 * Back cameras first: a door scans what is in front of the phone. A browser gives some cameras no name, and Android's read
 * "camera2 0, facing back", so those are numbered by the way they face.
 */
export const cameraChoices = (devices: ReadonlyArray<{ deviceId: string; label?: string | null }>): CameraChoice[] => {
  const counts: Record<CameraChoice["facing"], number> = { back: 0, front: 0, unknown: 0 };
  const order: Record<CameraChoice["facing"], number> = { back: 0, unknown: 1, front: 2 };

  return devices
    .map((device, index) => ({ device, index, label: String(device.label ?? "").trim(), facing: facingOf(String(device.label ?? "")) }))
    .sort((a, b) => order[a.facing] - order[b.facing] || a.index - b.index)
    .map(({ device, label, facing }) => {
      counts[facing] += 1;

      const named = label && !/^camera\d*\s*\d*,?\s*(facing)?/i.test(label);
      const word = facing === "back" ? "Back camera" : facing === "front" ? "Front camera" : "Camera";

      return { id: device.deviceId, facing, label: named ? label : `${word} ${counts[facing]}` };
    });
};
